/**
 * Admin: Service Accounts page.
 *
 * Manages service-account User rows + their API keys for orchestrator
 * authentication (R2 / Phase 5). Each service account has 0..N API keys;
 * mint shows the cleartext key exactly once in a copy-or-lose-it modal.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
} from '@/components/ui'
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Plus,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { apiClient } from '@/services/apiClient'

interface ServiceAccount {
  id: string
  display_name: string
  email: string
  is_service_account: boolean
  is_active: boolean
  created_at: string | null
}

interface ApiKeyMetadata {
  id: string
  name: string
  key_prefix: string
  key_suffix: string
  user_id: string
  created_at: string | null
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
}

interface ApiKeyMintResponse extends ApiKeyMetadata {
  /** Cleartext key — only present in the mint response, then gone forever. */
  key: string
}

export function ServiceAccountsPage() {
  const [accounts, setAccounts] = useState<ServiceAccount[]>([])
  const [keysByAccount, setKeysByAccount] = useState<Record<string, ApiKeyMetadata[]>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create SA modal state
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ display_name: '', email: '' })
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Mint key modal state
  const [mintTarget, setMintTarget] = useState<ServiceAccount | null>(null)
  const [mintName, setMintName] = useState('')
  const [isMinting, setIsMinting] = useState(false)
  const [mintError, setMintError] = useState<string | null>(null)

  // One-time reveal modal state
  const [reveal, setReveal] = useState<ApiKeyMintResponse | null>(null)
  const [copied, setCopied] = useState(false)

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyMetadata | null>(null)

  const fetchAccountsAndKeys = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const r = await apiClient.fetch('/api/admin/service-accounts')
      if (!r.ok) throw new Error(`Failed to load service accounts: ${r.statusText}`)
      const list: ServiceAccount[] = await r.json()
      setAccounts(list)

      // Fetch keys per account in parallel
      const keyEntries = await Promise.all(
        list.map(async (sa) => {
          const kr = await apiClient.fetch(`/api/admin/users/${sa.id}/api-keys`)
          if (!kr.ok) return [sa.id, []] as const
          return [sa.id, (await kr.json()) as ApiKeyMetadata[]] as const
        })
      )
      setKeysByAccount(Object.fromEntries(keyEntries))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccountsAndKeys()
  }, [fetchAccountsAndKeys])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setIsCreating(true)
    setCreateError(null)
    try {
      const r = await apiClient.fetch('/api/admin/service-accounts', {
        method: 'POST',
        body: JSON.stringify(createForm),
      })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `Failed: ${r.statusText}`)
      }
      setShowCreate(false)
      setCreateForm({ display_name: '', email: '' })
      await fetchAccountsAndKeys()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed')
    } finally {
      setIsCreating(false)
    }
  }

  const handleMint = async (e: FormEvent) => {
    e.preventDefault()
    if (!mintTarget) return
    setIsMinting(true)
    setMintError(null)
    try {
      const r = await apiClient.fetch(
        `/api/admin/users/${mintTarget.id}/api-keys`,
        {
          method: 'POST',
          body: JSON.stringify({ name: mintName }),
        }
      )
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `Failed: ${r.statusText}`)
      }
      const minted: ApiKeyMintResponse = await r.json()
      setMintTarget(null)
      setMintName('')
      setReveal(minted)
      setCopied(false)
      await fetchAccountsAndKeys()
    } catch (err) {
      setMintError(err instanceof Error ? err.message : 'Failed')
    } finally {
      setIsMinting(false)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    try {
      const r = await apiClient.fetch(`/api/admin/api-keys/${revokeTarget.id}`, {
        method: 'DELETE',
      })
      if (!r.ok && r.status !== 204) {
        throw new Error(`Failed: ${r.statusText}`)
      }
      setRevokeTarget(null)
      await fetchAccountsAndKeys()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke')
    }
  }

  const copyKey = async () => {
    if (!reveal) return
    try {
      await navigator.clipboard.writeText(reveal.key)
      setCopied(true)
    } catch {
      // fallthrough — user can still select the text manually
    }
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Service Accounts"
        subtitle="Manage service-account users and their API keys for external orchestrators (e.g. ai-expert-auto-deploy)."
        actions={
          <Button onClick={() => setShowCreate(true)} leftIcon={<UserPlus className="w-4 h-4" />}>
            New Service Account
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No service accounts yet. Create one to start issuing API keys.
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((sa) => {
            const keys = keysByAccount[sa.id] ?? []
            const activeKeys = keys.filter((k) => !k.revoked_at)
            return (
              <div
                key={sa.id}
                className="border border-border rounded-lg p-4 bg-background"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-medium text-foreground">
                        {sa.display_name}
                      </h2>
                      <Badge variant="secondary">Service Account</Badge>
                      {!sa.is_active && <Badge variant="default">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{sa.email}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setMintTarget(sa)
                      setMintName('')
                      setMintError(null)
                    }}
                    leftIcon={<Plus className="w-4 h-4" />}
                  >
                    Mint API Key
                  </Button>
                </div>

                {keys.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No API keys yet.</p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      API Keys ({activeKeys.length} active / {keys.length} total)
                    </p>
                    {keys.map((k) => (
                      <div
                        key={k.id}
                        className="flex items-center justify-between gap-4 p-3 rounded-sm bg-muted"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <KeyRound className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium text-sm text-foreground">
                              {k.name}
                            </span>
                            {k.revoked_at && (
                              <Badge variant="default" className="text-xs">
                                Revoked
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono mt-1">
                            {k.key_prefix}…{k.key_suffix}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Created {k.created_at ? new Date(k.created_at).toLocaleDateString() : '—'}
                            {k.last_used_at && (
                              <> · Last used {new Date(k.last_used_at).toLocaleDateString()}</>
                            )}
                            {k.revoked_at && (
                              <> · Revoked {new Date(k.revoked_at).toLocaleDateString()}</>
                            )}
                          </div>
                        </div>
                        {!k.revoked_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-8 h-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => setRevokeTarget(k)}
                            title="Revoke key"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create SA Modal */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>New Service Account</ModalTitle>
            <ModalDescription>
              Service accounts authenticate via API keys, not passwords. They're
              admin-by-default in v1.
            </ModalDescription>
          </ModalHeader>
          <form onSubmit={handleCreate}>
            <ModalBody className="space-y-3">
              {createError && (
                <Alert variant="destructive">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">
                  Display name
                </label>
                <Input
                  value={createForm.display_name}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, display_name: e.target.value }))
                  }
                  placeholder="auto-deploy-prod"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">
                  Identifier (email-like, can be `.local`)
                </label>
                <Input
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="auto-deploy-prod@neurealities.local"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  This is a unique label, not a deliverable address — `.local` and
                  `.internal` TLDs are accepted.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isCreating}>
                Create
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Mint Key Modal */}
      <Modal
        open={mintTarget !== null}
        onOpenChange={(o) => {
          if (!o) setMintTarget(null)
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Mint API Key</ModalTitle>
            <ModalDescription>
              For service account <strong>{mintTarget?.display_name}</strong>.
              The full key will be shown ONCE — make sure to save it.
            </ModalDescription>
          </ModalHeader>
          <form onSubmit={handleMint}>
            <ModalBody className="space-y-3">
              {mintError && (
                <Alert variant="destructive">
                  <AlertDescription>{mintError}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">
                  Key name
                </label>
                <Input
                  value={mintName}
                  onChange={(e) => setMintName(e.target.value)}
                  placeholder="prod-2026Q2"
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  A human-readable label so you can identify this key later.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMintTarget(null)}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={isMinting}>
                Mint Key
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* One-Time Reveal Modal */}
      <Modal
        open={reveal !== null}
        onOpenChange={(o) => {
          if (!o) {
            setReveal(null)
            setCopied(false)
          }
        }}
      >
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-success" />
              API Key Created
            </ModalTitle>
            <ModalDescription>
              <strong>This is the only time you'll see this key.</strong> Copy it
              now and store it somewhere safe (a secrets manager, your
              orchestrator config, etc.). After you close this dialog the key is
              gone — you'll have to mint a new one.
            </ModalDescription>
          </ModalHeader>
          <ModalBody className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                {reveal?.name}
              </label>
              <div className="flex items-center gap-2 p-3 rounded-sm bg-muted font-mono text-sm break-all">
                <span className="flex-1">{reveal?.key}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={copyKey}
                  leftIcon={
                    copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />
                  }
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                Use the <code className="font-mono">X-API-Key</code> header (preferred) or{' '}
                <code className="font-mono">Authorization: Bearer …</code> with this key
                when calling the API.
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                setReveal(null)
                setCopied(false)
              }}
            >
              I've saved it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Revoke Confirmation */}
      <Modal
        open={revokeTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRevokeTarget(null)
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Revoke API Key
            </ModalTitle>
            <ModalDescription>
              Revoking is immediate — the key will stop working on the next
              authenticated request. This cannot be undone.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground">
              Revoke <strong>{revokeTarget?.name}</strong> ({revokeTarget?.key_prefix}…{revokeTarget?.key_suffix})?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke} leftIcon={<Trash2 className="w-4 h-4" />}>
              Revoke
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
